// Scherm 15 — Verkennen "eenvoudig" (editie 11 in het platform, release 06-10).
//
// Ontwerp: docs/decisions/2026-09-10-verkennen-editie-11-eenvoudig.md, met
// editie 9 (eenvoud eerst: van de vraag naar de kaart) en editie 10 (het
// papieren prototype) ervoor. In het kort:
//
//   · DE DEUR: drie startvragen plus "of ga meteen naar de kaart".
//   · ÉÉN KAART op vaste plekken: gebieden → onderwerpen → skills. Echt inzoomen
//     (scroll/knijp), klik = één stand dieper, ← terug / Esc = één stand terug.
//   · HOOGUIT DRIE KNOPPEN per afstand plus "meer" (B1).
//   · DE GIDS: één zin, een klikbare lijst met de nummers van de kaart, één knop.
//   · HET KAARTJE per skill (I1) en BOUWEN als overlappende cirkels (J1).
//   · SPEEL HET JAAR AF.
//   · ELK VENSTER (kaart, gids, kaartje) naar volledig scherm of verborgen, met
//     een balkje om terug te halen; verborgen blijft verborgen (per kijker).
//
// De kaart tekent EenvoudigMotor (SVG, geen React per frame); de regels en de
// teksten staan in lib/eenvoudig.ts. De oude lagenkaart blijft onder "meer".

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BetaalmuurPil } from "../components/Bewijs";
import { EenvoudigMotor, type MotorStaat } from "../components/EenvoudigMotor";
import { LeesPaneel } from "../components/LeesPaneel";
import { SlidePanel } from "../components/ui";
import type { GraafKaartResponse, GraafRapportenResponse, GraafSkillkaartResponse } from "../contracts";
import { api } from "../lib/api";
import { useMeta } from "../lib/meta";
import { toonPropositie } from "../nieuw/propositie";
import {
  type Actie, type Doel, type KaartjeCijfers, type Positie, type Preset, KNOPPEN, MEER, PRESET_NAAM, type Stand,
  bouwWereld, alleParen, datumLang, geldig, gedeeld, gidsTekst, kaartjeCijfers, kort, legendaRegels, lichten, lijstNamen,
  maandLang, mv, nl, partners, sectorNaam, standaardPreset, vennData,
} from "../lib/eenvoudig";
import "./verkennen-eenvoudig.css";

type Venster = "kaart" | "gids" | "kaartje";
type VensterStand = "n" | "vol" | "weg";
const VENSTER_NAAM: Record<Venster, string> = { kaart: "Kaart", gids: "Gids", kaartje: "Kaartje" };
const SECTOREN = ["ict", "zorg", "maakindustrie", "hightech", "zakelijk", "publiek"];

function dagLang(iso: string): string {
  const [j, m, d] = iso.split("-").map(Number);
  const mnd = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
  return j && m && d ? `${d} ${mnd[m - 1]} ${j}` : iso;
}
function bewaar(k: string, v: unknown) { try { localStorage.setItem("verkennen-e11-" + k, JSON.stringify(v)); } catch { /* geen opslag */ } }
function haal<T>(k: string, d: T): T { try { const v = localStorage.getItem("verkennen-e11-" + k); return v == null ? d : (JSON.parse(v) as T); } catch { return d; } }

const IcoVol = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>);
const IcoKlein = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" /></svg>);
const IcoWeg = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14" /></svg>);
const IcoVraag = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9.6 9.4a2.5 2.5 0 1 1 3.4 2.4c-.6.3-1 .8-1 1.5v.6" /><path d="M12 17.2v.1" /></svg>);
const IcoHerstel = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="4" y="5" width="16" height="14" rx="2" /></svg>);

function VensterKnoppen({ naam, stand, zet, extra }: { naam: Venster; stand: VensterStand; zet: (n: Venster, s: VensterStand) => void; extra?: React.ReactNode }) {
  const vol = stand === "vol";
  const titel = vol ? "Terug naar normaal (Esc)" : "Volledig scherm";
  return (
    <span className="p-knoppen">
      {extra}
      <button type="button" className="ik" data-a="vol" title={titel} aria-label={titel} aria-pressed={vol} onClick={() => zet(naam, vol ? "n" : "vol")}>{vol ? <IcoKlein /> : <IcoVol />}</button>
      <button type="button" className="ik" data-a="weg" title="Verberg dit venster" aria-label="Verberg dit venster" onClick={() => zet(naam, "weg")}><IcoWeg /></button>
    </span>
  );
}

export function VerkennenEenvoudig({ sector, onSector, onVraag, onLagenkaart, onPlan }: {
  sector: string;
  onSector: (s: string) => void;
  onVraag?: (vraag: string) => void;
  /** de oude lagenkaart (ronde 4), bereikbaar onder "meer" tot de owner hem wegdoet */
  onLagenkaart: () => void;
  onPlan?: () => void;
}) {
  const meta = useMeta();
  const [kaart, setKaart] = useState<GraafKaartResponse | null>(null);
  const [rap, setRap] = useState<GraafRapportenResponse | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [start, setStart] = useState(true);
  const [doel, setDoel] = useState<Doel>(null);
  const [p, setP] = useState<Preset | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [bouw, setBouw] = useState<number[] | null>(null);
  const [speel, setSpeel] = useState(false);
  const [mi, setMi] = useState(0);
  const [loopt, setLoopt] = useState(false);
  const [f, setF] = useState<Positie>({ s: 1, t: 0, o: null });
  const [win, setWin] = useState<Record<Venster, VensterStand>>(() => {
    const w = haal<Record<Venster, VensterStand>>("win", { kaart: "n", gids: "n", kaartje: "n" });
    return { kaart: w.kaart === "weg" ? "weg" : "n", gids: w.gids === "weg" ? "weg" : "n", kaartje: w.kaartje === "weg" ? "weg" : "n" };
  });
  const [legenda, setLegenda] = useState(() => !haal("legenda", false));
  const [uitleg, setUitleg] = useState(false);
  const [gezoomd, setGezoomd] = useState(() => haal("gezoomd", false));
  const [alleenBevestigd, setAlleenBevestigd] = useState(false);
  const [skk, setSkk] = useState<GraafSkillkaartResponse | null>(null);
  const [lees, setLees] = useState<{ id: string; citaat?: string | null } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [meerOpen, setMeerOpen] = useState(false);
  const [tip, setTip] = useState<{ t: string; x: number; y: number } | null>(null);
  const [smal, setSmal] = useState(() => typeof window !== "undefined" && window.innerWidth <= 880);

  const svgRef = useRef<SVGSVGElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const motor = useRef<EenvoudigMotor | null>(null);
  const vorigeS = useRef<Stand>(1);

  // ── data ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let levend = true;
    setKaart(null); setRap(null); setFout(null);
    api.graafKaart({ sector, soorten: "bron,breder_smaller,onderwerp" }).then((d) => levend && setKaart(d)).catch((e) => levend && setFout(String(e)));
    // De rapporten maken de tellingen in "rapporten" mogelijk; zonder telt het scherm ontwikkelingen en skills en zegt het dat.
    api.graafRapporten({ sector }).then((d) => levend && setRap(d)).catch(() => levend && setRap({ sector, peil: new Date().toISOString().slice(0, 10), uitgevers: [], rapporten: [], onderwerpen: [] }));
    return () => { levend = false; };
  }, [sector]);
  const wereld = useMemo(() => (kaart && rap ? bouwWereld(kaart, rap, sector) : null), [kaart, rap, sector]);
  const venn = useMemo(() => (wereld && bouw ? vennData(wereld, bouw, rap) : null), [wereld, bouw, rap]);

  // De staat die de motor leest (altijd de laatste).
  const staat = useRef<MotorStaat>({ start, p, sel, bouw, speel, mi, alleenBevestigd, venn });
  staat.current = { start, p, sel, bouw, speel, mi, alleenBevestigd, venn };

  useEffect(() => {
    if (!wereld || !svgRef.current || !hostRef.current) return;
    const m = new EenvoudigMotor(svgRef.current, hostRef.current, wereld, () => staat.current, {
      onF: (nf) => setF(nf),
      onSkill: (i) => kiesSkillRef.current(i),
      onTip: (t, x, y) => setTip(t ? { t, x, y } : null),
      onGezoomd: () => { setGezoomd(true); bewaar("gezoomd", true); },
      onBeweeg: () => setMeerOpen(false),
    });
    motor.current = m;
    m.render();
    // Alleen in ontwikkeling: de proef (Playwright) leest hier de plekken af.
    if (import.meta.env.DEV) (window as unknown as { __vk11?: unknown }).__vk11 = { motor: m, wereld };
    return () => { m.destroy(); motor.current = null; };
  }, [wereld]);
  useEffect(() => { motor.current?.render(); }, [start, p, sel, bouw, speel, mi, alleenBevestigd, venn, win]);
  useEffect(() => { setMi((wereld?.maanden.length ?? 1) - 1); }, [wereld]);

  // Een andere afstand = de knop die daar hoort; afspelen blijft aan.
  useEffect(() => {
    if (f.s !== vorigeS.current) { vorigeS.current = f.s; if (p !== "speel") setP(standaardPreset(f.s, doel)); }
    else if (p && !geldig(p, f.s)) { setP(standaardPreset(f.s, doel)); }
    if (sel !== null && wereld && (f.s !== 3 || wereld.skills[sel]?.ow !== f.o)) { setSel(null); if (p === "wie") setP("student"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.s, f.t, f.o]);

  // Het kaartje: de bronnen van de gekozen skill.
  const gekozen = wereld && sel !== null ? wereld.skills[sel] ?? null : null;
  useEffect(() => {
    if (!gekozen) { setSkk(null); return; }
    let levend = true; setSkk(null);
    api.graafSkillkaart(gekozen.id).then((b) => levend && setSkk(b)).catch(() => levend && setSkk({ skill: gekozen.id, rapporten: 0, uitgevers: 0, van: null, tot: null, wie: [], zin: null }));
    return () => { levend = false; };
  }, [gekozen]);
  const kj: KaartjeCijfers | null = useMemo(() => (wereld && gekozen && skk ? kaartjeCijfers(wereld, gekozen, skk) : null), [wereld, gekozen, skk]);

  // Toast.
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 4500); return () => clearTimeout(t); }, [toast]);
  useEffect(() => { const r = () => setSmal(window.innerWidth <= 880); window.addEventListener("resize", r); return () => window.removeEventListener("resize", r); }, []);

  // ── vensters ────────────────────────────────────────────────────────────
  const zetVenster = useCallback((naam: Venster, st: VensterStand) => {
    setWin((w) => {
      const n = { ...w };
      if (st === "vol") (Object.keys(n) as Venster[]).forEach((k) => { if (n[k] === "vol") n[k] = "n"; });
      n[naam] = st;
      bewaar("win", { kaart: n.kaart === "weg" ? "weg" : "n", gids: n.gids === "weg" ? "weg" : "n", kaartje: n.kaartje === "weg" ? "weg" : "n" });
      return n;
    });
    if (st === "weg") setToast(({ kaart: "De kaart", gids: "De gids", kaartje: "Het kaartje" } as const)[naam] + " is verborgen. Terughalen kan met de knop bovenaan.");
  }, []);
  const ietsVol = win.kaart === "vol" || win.gids === "vol" || win.kaartje === "vol";
  useEffect(() => {
    document.body.classList.toggle("vk11-iets-vol", ietsVol);
    const t = setTimeout(() => motor.current?.resize(false), 0);
    return () => { clearTimeout(t); document.body.classList.remove("vk11-iets-vol"); };
  }, [ietsVol, win.kaart, win.gids, sel]);

  // ── bediening ───────────────────────────────────────────────────────────
  const stopLoop = useCallback(() => setLoopt(false), []);
  const zetSpeel = useCallback((aan: boolean) => {
    if (aan) { setSpeel((was) => { if (!was && wereld) setMi(wereld.maanden.length - 1); return true; }); }
    else { setSpeel(false); setLoopt(false); }
  }, [wereld]);
  useEffect(() => {
    if (!loopt || !wereld) return;
    const t = setInterval(() => setMi((m) => { if (m >= wereld.maanden.length - 1) { setLoopt(false); return m; } return m + 1; }), 170);
    return () => clearInterval(t);
  }, [loopt, wereld]);

  const kiesSkill = useCallback((i: number | null) => {
    setSel(i);
    if (i !== null) {
      setP("wie");
      setWin((w) => (w.kaartje === "weg" ? { ...w, kaartje: "n" } : w));
      const rechts = (hostRef.current?.clientWidth ?? 0) > 560 && !smal;
      motor.current?.toonSkill(i, rechts);
    } else setP((q) => (q === "wie" ? "student" : q));
  }, [smal]);
  const kiesSkillRef = useRef(kiesSkill);
  kiesSkillRef.current = (i: number | null) => kiesSkill(i !== null && i === staat.current.sel ? null : i);

  const kies = useCallback((np: Preset | null) => {
    if (np === "wie" && sel === null && f.s === 3 && wereld && f.o !== null) {
      const o = wereld.onderwerpen[f.o]!; const eerste = o.sk[0];
      if (eerste !== undefined) { kiesSkill(eerste); return; }
    }
    if (np !== "wie" && sel !== null) setSel(null);
    setP(np); zetSpeel(np === "speel");
  }, [sel, f, wereld, kiesSkill, zetSpeel]);

  const startBouw = useCallback((lijst?: number[]) => {
    setSel(null); zetSpeel(false); setP((q) => (q === "wie" ? "student" : q));
    setBouw((lijst ?? (f.s === 3 && f.o !== null && wereld ? partners(wereld, f.o) : [])).slice(0, 3));
    setMeerOpen(false);
  }, [f, wereld, zetSpeel]);
  const stopBouw = useCallback(() => { setBouw(null); setTimeout(() => motor.current?.render(), 0); }, []);

  const naarDeur = useCallback(() => { setStart(true); setBouw(null); setSel(null); zetSpeel(false); }, [zetSpeel]);
  const beginMet = useCallback((d: "nieuw" | "student" | "bouw" | "kaart") => {
    setStart(false); setDoel(d === "kaart" ? null : d); setP(d === "nieuw" ? "nieuw" : null); setSel(null); setBouw(d === "bouw" ? [] : null); zetSpeel(false);
    vorigeS.current = 1;
    setTimeout(() => { const m = motor.current; if (!m) return; m.resize(true); m.cam = m.fitAll(); m.render(); }, 0);
  }, [zetSpeel]);

  const terug = useCallback(() => {
    if (sel !== null) { kiesSkill(null); return; }
    if (bouw) { stopBouw(); return; }
    const m = motor.current;
    if (f.s === 3) m?.naarGebied(f.t); else if (f.s === 2) m?.naarVeld(); else naarDeur();
  }, [sel, bouw, f, kiesSkill, stopBouw, naarDeur]);

  const ietsAnders = useCallback(() => {
    const l = [...KNOPPEN[f.s], ...MEER[f.s]], i = p ? l.indexOf(p) : -1;
    kies(l[(i + 1) % l.length]!);
  }, [f.s, p, kies]);

  const doe = useCallback((a: Actie) => {
    const m = motor.current;
    if (a.soort === "gebied") m?.naarGebied(a.i);
    else if (a.soort === "onderwerp") { if (sel !== null) setSel(null); m?.naarOnderwerp(a.i); }
    else if (a.soort === "skill") kiesSkill(a.i);
    else if (a.soort === "bouw") startBouw(a.ids);
    else if (a.soort === "kies") kies(a.p);
    else if (a.soort === "chat") { if (onVraag) onVraag(a.vraag); else setToast("Vraag voor de chat: ‘" + a.vraag + "’"); }
    else if (a.soort === "speel") { if (!speel) kies("speel"); if (loopt) stopLoop(); else { if (wereld && mi >= wereld.maanden.length - 1) setMi(0); setLoopt(true); } }
  }, [sel, kiesSkill, startBouw, kies, onVraag, speel, loopt, stopLoop, wereld, mi]);

  // Esc: eerst volledig scherm dicht, dan het menu, dan één stand terug.
  useEffect(() => {
    const op = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (lees) return; // het leespaneel sluit zichzelf
      if (ietsVol) { setWin((w) => ({ kaart: w.kaart === "vol" ? "n" : w.kaart, gids: w.gids === "vol" ? "n" : w.gids, kaartje: w.kaartje === "vol" ? "n" : w.kaartje })); e.preventDefault(); return; }
      if (meerOpen) { setMeerOpen(false); return; }
      const tgt = e.target instanceof Element ? e.target : null;
      if (start || (tgt && tgt.closest("textarea,input,select"))) return;
      if (tgt && tgt !== document.body && !tgt.closest(".vk11")) return;
      terug();
    };
    document.addEventListener("keydown", op);
    return () => document.removeEventListener("keydown", op);
  }, [ietsVol, meerOpen, start, terug, lees]);
  useEffect(() => {
    if (!meerOpen) return;
    const dicht = (e: MouseEvent) => { if (!(e.target as Element).closest?.(".meer")) setMeerOpen(false); };
    document.addEventListener("click", dicht);
    return () => document.removeEventListener("click", dicht);
  }, [meerOpen]);

  // ── afgeleide teksten ───────────────────────────────────────────────────
  const gids = useMemo(() => (wereld && !start && !bouw ? gidsTekst(wereld, p, f, { doel, sel, loopt, alleenBevestigd, kaartje: kj }) : null), [wereld, start, bouw, p, f, doel, sel, loopt, alleenBevestigd, kj]);
  const toonLegenda = !!wereld && !start && ((legenda && f.s === 1 && !bouw) || uitleg);
  const sNaam = sectorNaam(sector);

  if (fout) return (<div className="vk11"><div className="vk"><p className="vk-fout">De kaart kon niet laden: {fout}</p></div></div>);

  const kaartjeAan = !start && !bouw && gekozen !== null && win.kaartje !== "weg";
  const verborgen = (["kaart", "gids", "kaartje"] as Venster[]).filter((k) => win[k] === "weg" && (k !== "kaartje" || (gekozen !== null && !bouw)));
  const knoppen = KNOPPEN[f.s], meer = MEER[f.s];
  const datumTekst = rap?.peil ? dagLang(rap.peil) : "";

  return (
    <div className="vk11">
      <div className="vk">
        <div className="vk-r1">
          <div className="kruimel">
            {start ? <span className="kr-hier">Verkennen</span> : (
              <>
                <button type="button" className="kr-terug" onClick={terug} title="Eén stand terug (Esc)">← terug</button>
                <span className="kr-pad">
                  {(() => {
                    const delen: [string, (() => void) | null][] = [[sNaam, () => motor.current?.naarVeld()]];
                    if (bouw) delen.push(["Bouwen", null]);
                    else if (wereld) {
                      if (f.s >= 2) delen.push([wereld.gebieden[f.t]?.naam ?? "", () => motor.current?.naarGebied(f.t)]);
                      if (f.s === 3 && f.o !== null) delen.push([wereld.onderwerpen[f.o]?.naam ?? "", () => { kiesSkill(null); motor.current?.naarOnderwerp(f.o!); }]);
                      if (gekozen) delen.push([kort(gekozen.naam, 30), null]);
                    }
                    return delen.map(([naam, fn], i) => (
                      <span key={i} className="kr-deel">
                        {i > 0 && <i>›</i>}
                        {i === delen.length - 1 || !fn ? <span className="kr-hier">{naam}</span> : <button type="button" className="kr-link" onClick={fn}>{naam}</button>}
                      </span>
                    ));
                  })()}
                </span>
              </>
            )}
          </div>
          <div className="r1-rechts">
            {!!wereld?.aantalBevestigd && !start && (
              <label className="bev-filter" title="Tel alleen ontwikkelingen die bevestigd zijn (weging v3): meer dan één soort bron, met werkvloer of cijfers.">
                <input type="checkbox" checked={alleenBevestigd} onChange={(e) => setAlleenBevestigd(e.target.checked)} /> alleen bevestigd
              </label>
            )}
            <select className="sector-keuze" aria-label={toonPropositie(meta) ? "Propositie" : "Sector"} value={sector} onChange={(e) => { onSector(e.target.value); setStart(true); }}>
              {/* ADR 2026-10-23-ict-los-platform: de zichtbare proposities voorop; met
                  alleen ICT zichtbaar precies de oude zes. */}
              {/* Pilot gereed 26-10 (1C): ICT alleen als ICT zichtbaar is. */}
              {[...new Set([...(meta?.zichtbareSectoren ?? []), ...SECTOREN.filter((s) => s !== "ict" || (meta?.zichtbareSectoren ?? ["ict"]).includes("ict"))])].map((s) => (
                <option key={s} value={s}>{meta?.proposities?.find((p) => p.sector === s)?.naam ?? sectorNaam(s)}</option>
              ))}
            </select>
            <span className="schakel"><span className="aan">eenvoudig</span><span className="uit" title="Komt na de test met twee collega’s">uitgebreid</span></span>
          </div>
        </div>

        {!start && (
          <div className="vk-r2">
            <div className="presets">
              {bouw ? (
                <>
                  <span className="bouwlabel">Bouw met</span>
                  {bouw.map((i) => (
                    <button key={i} type="button" className="chip" style={{ borderColor: wereld?.onderwerpen[i]?.kleur }} title={"Haal " + wereld?.onderwerpen[i]?.naam + " weg"} onClick={() => setBouw(bouw.filter((x) => x !== i))}>{wereld?.onderwerpen[i]?.naam} ×</button>
                  ))}
                  {bouw.length < 3 && wereld && (
                    <select className="chip plus" aria-label="Voeg een onderwerp toe" value="" onChange={(e) => { if (e.target.value !== "") setBouw([...bouw, Number(e.target.value)]); }}>
                      <option value="">+ onderwerp</option>
                      {wereld.gebieden.map((g) => (
                        <optgroup key={g.i} label={g.naam}>
                          {g.ow.filter((i) => !bouw.includes(i)).map((i) => <option key={i} value={i}>{wereld.onderwerpen[i]!.naam}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  )}
                </>
              ) : (
                <>
                  {knoppen.map((q) => <PresetKnop key={q} q={q} p={p} kies={kies} />)}
                  {p && meer.includes(p) && <PresetKnop q={p} p={p} kies={kies} />}
                  <div className="meer">
                    <button type="button" className="pill meerknop" aria-haspopup="true" aria-expanded={meerOpen} title="Meer manieren om te kijken" onClick={(e) => { e.stopPropagation(); setMeerOpen(!meerOpen); }}>meer ▾</button>
                    {meerOpen && (
                      <div className="meer-menu" role="menu">
                        {meer.map((q) => <button key={q} type="button" role="menuitem" className={"menu-item" + (p === q ? " aan" : "")} onClick={() => { setMeerOpen(false); kies(q); }}>{PRESET_NAAM[q]}</button>)}
                        <button type="button" role="menuitem" className="menu-item" onClick={() => startBouw()}>Bouw een opleiding op …</button>
                        <button type="button" role="menuitem" className="menu-item stil" onClick={() => { setMeerOpen(false); onLagenkaart(); }}>De uitgebreide lagenkaart (oud)</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="r2-rechts">
              {bouw ? <button type="button" className="bouwknop" onClick={stopBouw}>← Terug naar de kaart</button>
                : <button type="button" className="bouwknop" title="Kies twee of drie onderwerpen en zie wat ze delen" onClick={() => startBouw()}>Bouw ▸</button>}
            </div>
          </div>
        )}

        {!start && verborgen.length > 0 && (
          <div className="dock">
            <span className="lbl">Verborgen</span>
            {verborgen.map((k) => (
              <button key={k} type="button" className="dock-knop" title={"Laat " + VENSTER_NAAM[k].toLowerCase() + " weer zien"} onClick={() => zetVenster(k, "n")}><IcoHerstel /> {VENSTER_NAAM[k]}</button>
            ))}
            {verborgen.length > 1 && <button type="button" className="dock-knop" onClick={() => { setWin({ kaart: "n", gids: "n", kaartje: "n" }); bewaar("win", { kaart: "n", gids: "n", kaartje: "n" }); }}>Alles tonen</button>}
          </div>
        )}

        {start && (
          <div className="deur">
            <div className="kl">Verkennen</div>
            <h3 className="deur-v">Waar wil je mee beginnen?</h3>
            <div className="vragen">
              <button type="button" className="vraag" disabled={!wereld} onClick={() => beginMet("nieuw")}><b>Wat komt eraan in {sNaam}?</b><span className="sub">nieuwe ontwikkelingen, en waar ze zitten</span></button>
              <button type="button" className="vraag" disabled={!wereld} onClick={() => beginMet("student")}><b>Wat moet een student straks kunnen?</b><span className="sub">de skills die het vaakst terugkomen</span></button>
              <button type="button" className="vraag" disabled={!wereld} onClick={() => beginMet("bouw")}><b>Waar kan ik een opleiding op bouwen?</b><span className="sub">onderwerpen die bij elkaar horen</span></button>
            </div>
            <button type="button" className="direct" disabled={!wereld} onClick={() => beginMet("kaart")}>of ga meteen naar de kaart</button>
            <div className="voet">
              {!wereld ? "De kaart laadt…" : wereld.metRapporten ? `Gemaakt uit ${nl(wereld.totaalRapporten)} rapporten · stand ${datumTekst}` : `Gemaakt uit ${nl(wereld.onderwerpen.reduce((a, o) => a + o.punten, 0))} ontwikkelingen en skills`}
            </div>
            {wereld && !wereld.gebieden.length && <div className="voet">Voor {sNaam} staan er nog geen onderwerpen op de kaart.</div>}
          </div>
        )}

        <div className={"vk-main" + (win.kaart === "weg" ? " zonder-kaart" : "") + (win.gids === "weg" ? " zonder-gids" : "")} hidden={start}>
          <section className={"paneel kaartvenster" + (win.kaart === "vol" ? " vol" : "") + (win.kaart === "weg" ? " weg" : "")} aria-label="Kaart">
            <div className="p-bar">
              <span className="p-titel">Kaart</span>
              {win.kaart === "vol" && !bouw && (
                <span className="alleen-vol">
                  <select className="vol-select" aria-label="Laat zien" value={p ?? ""} onChange={(e) => kies((e.target.value || null) as Preset | null)}>
                    <option value="">Laat zien: gewone kaart</option>
                    {[...knoppen, ...meer].map((q) => <option key={q} value={q}>{PRESET_NAAM[q]}</option>)}
                  </select>
                </span>
              )}
              <VensterKnoppen naam="kaart" stand={win.kaart} zet={zetVenster} />
            </div>
            <div className="kaart" ref={hostRef} tabIndex={0} aria-label="De kaart: scroll of knijp om te zoomen, pijltjes om te schuiven, plus en min voor een stand dieper of terug"
              onKeyDown={(e) => {
                const m = motor.current; if (!m || start || bouw) return;
                if (e.key === "+" || e.key === "=") m.zoomIn(); else if (e.key === "-") m.zoomUit();
                else if (e.key === "ArrowLeft") m.schuif(-1, 0); else if (e.key === "ArrowRight") m.schuif(1, 0);
                else if (e.key === "ArrowUp") m.schuif(0, -1); else if (e.key === "ArrowDown") m.schuif(0, 1);
                else return;
                e.preventDefault();
              }}>
              <svg ref={svgRef} className="vk-svg" role="img" aria-label={"De kaart van Verkennen, " + sNaam} />
              {!wereld && <div className="laadt">De kaart laadt…</div>}
              {!bouw && (
                <div className="zoomctl">
                  <button type="button" title="Eén stand dieper" aria-label="Inzoomen" onClick={() => { if (sel !== null) setSel(null); motor.current?.zoomIn(); }}>+</button>
                  <button type="button" title="Eén stand terug" aria-label="Uitzoomen" onClick={() => { if (sel !== null) kiesSkill(null); motor.current?.zoomUit(); }}>−</button>
                </div>
              )}
              {!bouw && f.s === 1 && !gezoomd && <div className="hint">Scroll of knijp om in te zoomen</div>}
              {tip && <div className="tip" style={{ left: tip.x, top: tip.y }}>{tip.t}</div>}
            </div>
            {kaartjeAan && gekozen && wereld && (
              <section className={"paneel kaartje" + (win.kaartje === "vol" ? " vol" : "")} aria-label="Over deze skill">
                <div className="p-bar"><span className="p-titel">Over deze skill</span><VensterKnoppen naam="kaartje" stand={win.kaartje} zet={zetVenster} /></div>
                <div className="kj">
                  <div className="kj-titel">{gekozen.naam}</div>
                  {!kj ? <p className="kj-noot">Ik zoek de bronnen…</p> : (
                    <>
                      <div className="kj-rij"><span className="kj-l">Gevraagd door</span><span>{mv(kj.trends, "ontwikkeling", "ontwikkelingen")}, {kj.nieuw} van het laatste jaar{kj.bevestigd ? `; ${kj.bevestigd} bevestigd` : ""}</span></div>
                      <div className="kj-rij"><span className="kj-l">Staat in</span><span>{mv(kj.docs, "rapport", "rapporten")} van {mv(kj.uitg, "uitgever", "uitgevers")}</span></div>
                      {kj.lijkt && <div className="kj-rij"><span className="kj-l">Lijkt op</span><span>{kj.lijkt[0]} {kj.lijkt[0] === 1 ? "skill" : "skills"}, zoals ‘{kj.lijkt[1]}’</span></div>}
                      {kj.van && <div className="kj-rij"><span className="kj-l">Sinds</span><span>{datumLang(kj.van)} in de bronnen{kj.tot ? " · laatste: " + datumLang(kj.tot) : ""}</span></div>}
                      {kj.pubs.length > 0 && (
                        <div className="kj-wie">
                          <span className="kj-l">Wie zegt dat</span>
                          <div className="kj-uitg">
                            {kj.pubs.map(([u, n]) => <span key={u}>{u} {n}</span>)}
                            {kj.uitg > kj.pubs.length && <span>+{kj.uitg - kj.pubs.length} andere</span>}
                          </div>
                          {kj.citaat && (
                            <>
                              <blockquote className="kj-citaat" style={{ borderLeftColor: wereld.onderwerpen[gekozen.ow]?.kleur }}>{kj.citaat.zin}</blockquote>
                              <div className="kj-bron">{kj.citaat.uitgever}{kj.citaat.datum ? " · " + datumLang(kj.citaat.datum) : ""}{kj.citaat.alineaId && <> · <button type="button" className="kj-lees" onClick={() => setLees({ id: kj.citaat!.alineaId!, citaat: kj.citaat!.zin })}>lees de alinea →</button></>}</div>
                              {kj.citaat.achterBetaalmuur && <div className="kj-bron"><BetaalmuurPil bron={kj.citaat} /></div>}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {gekozen.dub > 0 && <p className="kj-noot">Deze naam staat {gekozen.dub + 1} keer in de data; ik toon er één.</p>}
                </div>
              </section>
            )}
            {speel && !bouw && wereld && (
              <div className="tijd">
                <button type="button" onClick={() => doe({ soort: "speel" })}>{loopt ? "❚❚ Pauze" : "▶ Speel af"}</button>
                <input type="range" min={0} max={wereld.maanden.length - 1} step={1} value={mi} aria-label="Maand" onChange={(e) => { setMi(Number(e.target.value)); stopLoop(); }} />
                <span className="mnd">{maandLang(wereld.maanden[mi] ?? wereld.nu)}</span>
              </div>
            )}
          </section>

          <section className={"paneel gidsvenster" + (win.gids === "vol" ? " vol" : "") + (win.gids === "weg" ? " weg" : "")} aria-label="Gids">
            <div className="p-bar">
              <span className="p-titel"><span className="g-dot" />Gids</span>
              <VensterKnoppen naam="gids" stand={win.gids} zet={zetVenster}
                extra={<button type="button" className="ik" data-a="uitleg" title="Hoe lees ik de kaart?" aria-label="Hoe lees ik de kaart?" onClick={() => setUitleg(!uitleg)}><IcoVraag /></button>} />
            </div>
            <div className="gids" aria-live="polite">
              {toonLegenda && wereld && (
                <div className="g-leg">
                  <b>Zo lees je de kaart</b>
                  <ul>{legendaRegels(wereld, f, !!bouw).map((r) => <li key={r}>{r}</li>)}</ul>
                  <button type="button" className="g-link" onClick={() => { setLegenda(false); setUitleg(false); bewaar("legenda", true); }}>begrepen</button>
                </div>
              )}
              {bouw && wereld ? <GidsBouw wereld={wereld} venn={venn} bouw={bouw} setBouw={setBouw} stopBouw={stopBouw} onPlan={onPlan} toast={setToast} />
                : gids && (
                  <>
                    <p className="g-zin"><b>{gids.zin}</b>{gids.rest ? " " + gids.rest : ""}</p>
                    {!!gids.lijst?.length && (
                      <ol className="g-lijst">
                        {gids.lijst.map((it, j) => (
                          <li key={j}><button type="button" className="g-item" onClick={() => doe(it.actie)}><i className={it.nr !== null ? "nr" : "nr leeg"}>{it.nr ?? ""}</i><span className="nm">{it.naam}</span><span className="vl">{it.waarde}</span></button></li>
                        ))}
                      </ol>
                    )}
                    {(gids.klein ?? []).map((t) => <p key={t} className="g-klein">{t}</p>)}
                    {!wereld?.metRapporten && <p className="g-klein">De rapporten zijn niet geladen: ik tel ontwikkelingen en skills.</p>}
                    <div className="g-knoppen">
                      {gids.knop && <button type="button" className="knop zwart" onClick={() => doe(gids.knop!.actie)}>{gids.knop.tekst}</button>}
                      <button type="button" className="knop" title="Laat de kaart op een andere manier zien" onClick={ietsAnders}>iets anders</button>
                    </div>
                  </>
                )}
            </div>
          </section>
        </div>
        {toast && <div className="toast" role="status">{toast}</div>}
      </div>

      <SlidePanel open={lees !== null} onClose={() => setLees(null)} terugLabel="Terug naar de kaart">
        {lees && <LeesPaneel alineaId={lees.id} citaat={lees.citaat} onClose={() => setLees(null)} onOpenAlinea={(id, c) => setLees({ id, citaat: c })} />}
      </SlidePanel>
    </div>
  );
}

function PresetKnop({ q, p, kies }: { q: Preset; p: Preset | null; kies: (p: Preset | null) => void }) {
  return <button type="button" className={"pill" + (p === q ? " aan" : "")} aria-pressed={p === q} onClick={() => kies(p === q ? null : q)}>{PRESET_NAAM[q]}</button>;
}

function GidsBouw({ wereld, venn, bouw, setBouw, stopBouw, onPlan, toast }: {
  wereld: ReturnType<typeof bouwWereld>; venn: ReturnType<typeof vennData> | null; bouw: number[];
  setBouw: (b: number[]) => void; stopBouw: () => void; onPlan?: () => void; toast: (t: string) => void;
}) {
  const n = bouw.length;
  if (n < 2) {
    const voor: [number, number, number][] = n === 0 ? alleParen(wereld).slice(0, 3) : partners(wereld, bouw[0]!).slice(1).map((j) => [bouw[0]!, j, gedeeld(wereld, bouw[0]!, j)]);
    return (
      <>
        <p className="g-zin"><b>{n === 0 ? "Kies twee of drie onderwerpen; ik laat zien wat ze delen." : "Kies er nog één of twee bij."}</b>{n === 0 ? " Of begin met een paar dat veel deelt:" : " Deze horen het meest bij " + wereld.onderwerpen[bouw[0]!]!.naam + ":"}</p>
        <ol className="g-lijst">
          {voor.map((q) => (
            <li key={q[0] + "-" + q[1]}><button type="button" className="g-item" onClick={() => setBouw(n === 0 ? [q[0], q[1]] : [...bouw, q[1]])}>
              <i className="nr leeg">+</i><span className="nm">{n === 0 ? wereld.onderwerpen[q[0]]!.naam + " + " + wereld.onderwerpen[q[1]]!.naam : wereld.onderwerpen[q[1]]!.naam}</span><span className="vl">{q[2]} skills</span>
            </button></li>
          ))}
        </ol>
        <div className="g-knoppen"><button type="button" className="knop" onClick={stopBouw}>terug naar de kaart</button></div>
      </>
    );
  }
  if (!venn) return null;
  const namen = bouw.map((i) => wereld.onderwerpen[i]!.naam);
  return (
    <>
      <p className="g-zin"><b>{n === 2 ? namen[0] + " en " + namen[1] + " delen " + venn.twee + " skills." : lijstNamen(namen) + " delen " + venn.twee + " skills; " + venn.alle + " komen in alle drie terug."}</b> Dat is de kern van een opleiding die ze samen afdekt.</p>
      <div className="lichten">
        {lichten(wereld, venn).map((l) => (
          <div key={l.label} className={"licht " + l.band}><i aria-hidden="true" /><div><b>{l.label}</b><span>{l.tekst}</span></div></div>
        ))}
      </div>
      {venn.gedeeld.length > 0 && (
        <>
          <p className="g-kop2">{n === 3 && venn.alle ? "In alle drie, onder andere:" : "Gedeeld, onder andere:"}</p>
          <ul className="g-namen">{venn.gedeeld.slice(0, 5).map(([id]) => <li key={id}>{wereld.punt.get(id)?.naam ?? id}</li>)}</ul>
        </>
      )}
      <div className="g-knoppen">
        <button type="button" className="knop zwart" onClick={() => (onPlan ? onPlan() : toast("Het plan-scherm (MBP) begint hier straks met deze " + venn.twee + " skills."))}>Maak er een plan van</button>
        <button type="button" className="knop" onClick={stopBouw}>terug naar de kaart</button>
      </div>
    </>
  );
}

